import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { FormControl } from '@angular/forms';
import { take, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from '@node_modules/rxjs';
import { Subject } from 'rxjs';
import { MatSelect } from '@angular/material/select';
import {
  ActivityDaysServiceProxy,
  AllocatedPlantDto,
  AllocatePlantForActivityDayInput,
  UpdateAllocatePlantListForActivityDayInput,
} from '@shared/service-proxies/service-proxies';

@Component({
  selector: 'app-assign-plants',
  templateUrl: './assign-plants.component.html',
  styleUrls: ['./assign-plants.component.less'],
})
export class AssignPlantsComponent
  extends AppComponentBase
  implements OnInit, AfterViewInit {
  @ViewChild('plantsSelector', { static: true }) plantsSelector: MatSelect;

  @Input() allowedPlants: any;
  @Input() activityDayId: string;
  _allocatedPlants: any;
  @Input() set allocatedPlants(value: any) {
    this._allocatedPlants = value;
    const allocated = this.getValueforPlantsFormCtrl()
    this.plantsFormCtrl.setValue(allocated);
  }
  get allocatedPlants(): any {
    return this._allocatedPlants;
  }
  @Input() anyResourceSelected: boolean;

  @Output()
  onAssignedCounterUpdate: EventEmitter<number> = new EventEmitter<number>();
  @Input() componentMode: 'allocationSheet' | 'default' = 'default';

  @Output() showActivityPanel: EventEmitter<any> = new EventEmitter<any>();
  @Input() controlDisabled: boolean;

  public plantsFormCtrl: FormControl = new FormControl();
  public plantsFilterFormCtrl: FormControl = new FormControl();
  public filteredPlants: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  protected _onDestroy = new Subject<void>();

  constructor(
    injector: Injector,
    private _activityDaysServiceProxy: ActivityDaysServiceProxy,
    private cdr: ChangeDetectorRef
  ) {
    super(injector);
  }

  updateTotals() {
    let personnelSum = 0;
    this.plantsFormCtrl.value.forEach(
      (item) => (personnelSum += parseInt(item.hours))
    );
    this.onAssignedCounterUpdate.emit(personnelSum);
  }

  showError(): boolean {
    const selectedPlant = this.plantsFormCtrl?.value;
    const total = selectedPlant && selectedPlant.length >0 ? selectedPlant.map(x=>x.hours).reduce((a, b) => { return a + b }):0;
    return total<=0
  }

  addOrRemovePlant(plant) {
    if (
      this.plantsFormCtrl.value.some(
        (item) => item.serialNumber === plant.serialNumber
      )
    ) {
      //add to list
      const params = new AllocatePlantForActivityDayInput({
        activityDayId: this.activityDayId,
        plant: new AllocatedPlantDto({
          actualHours: 0,
          plantId: plant.id,
        }),
      });
      this._activityDaysServiceProxy.allocatePlant(params).subscribe(() => {});
    } else {
      //remove from list
      this._activityDaysServiceProxy
        .removeAllocatedPlant(this.activityDayId, plant.id)
        .subscribe(() => {});
    }

    this.updateTotals();
  }

  ngAfterViewInit() {
    this.setInitialPlantSelectorValues();
  }

  setInitialPlantSelectorValues() {
    this.filteredPlants
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.plantsSelector.compareWith = (a: any, b: any) => {
          return a && b && a.id === b.id;
        };
      });
  }

  ngOnInit(): void {
    if (this.allowedPlants) {
      const allowed = [...this.allowedPlants].map((item) => {
        let hrs = 0;

        this.allocatedPlants.allocatedPlants.forEach((allocated) => {
          if (allocated.plantId === item.id) {
            hrs = allocated.actualHours;
          }
        });

        return {
          ...item,
          hours: hrs,
        };
      });

      this.allowedPlants = allowed;
      this.cdr.detectChanges();
    }

    if (this.allowedPlants) {
      this.filteredPlants.next(this.allowedPlants.slice());
    }

    if (this.allocatedPlants && this.allocatedPlants.allocatedPlants) {
      const allocated = this.getValueforPlantsFormCtrl();
      this.plantsFormCtrl.setValue(allocated);
    }

    // this.updateTotals();

    this.plantsFilterFormCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterPlants();
      });
  }

  getValueforPlantsFormCtrl(){
    return [...this.allocatedPlants.allocatedPlants].map(
      (item) => {
        return {
          // ...item,
          id: item.plantDetails.id,
          serialNumber: item.plantDetails.serialNumber,
          supplierName: item.plantDetails.supplierName,
          description : item.plantDetails.description,
          hours: item.actualHours,
        };
      }
    );
  }
  protected filterPlants() {
    if (!this.allowedPlants) {
      return;
    }

    let search = this.plantsFilterFormCtrl.value;
    if (!search) {
      this.filteredPlants.next(this.allowedPlants.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredPlants.next(
      this.allowedPlants?.filter(
        (plant) =>
          `${plant.description.toLowerCase()}`.includes(search) ||
          `${plant.serialNumber.toLowerCase()}`.includes(search) ||
          `${plant.description.toLowerCase()} (${plant.serialNumber.toLowerCase()})`.includes(
            search
          )
      )
    );
  }

  removeItem(plantId: string) {
    this.message.confirm('', this.l('AreYouSure'), (isConfirmed) => {
      if (isConfirmed) {
        const filtered = this.plantsFormCtrl.value?.filter(
          (plant) => plant.id != plantId
        );
        this.plantsFormCtrl.setValue(filtered);
        this._activityDaysServiceProxy
          .removeAllocatedPlant(this.activityDayId, plantId)
          .subscribe(() => {
            this.updateTotals();
          });

        this.cdr.detectChanges();
      }
    });
  }

  updateAllocatedPlantsList() {
    const params = new UpdateAllocatePlantListForActivityDayInput({
      activityDayId: this.activityDayId,
      plants: [...this.plantsFormCtrl.value].map((item) => {
        return new AllocatedPlantDto({
          actualHours: parseInt(item.hours) || 0,
          plantId: item.id,
        });
      }),
    });

    this._activityDaysServiceProxy
      .updateAllocatePlantList(params)
      .subscribe(() => {
        this.updateTotals();
      });
  }

  isAllocationSheet() {
    return this.componentMode === 'allocationSheet';
  }

  edit() {
    this.showActivityPanel.emit();
  }
}
